// @ts-check
import React, { useEffect, useState } from 'react';
import { Page, Utils, DocumentPreview, Icon } from 'ecrion-components';
import { withAuth } from 'ecrion-components';
import { useAsync } from 'react-async';
import Snackbar from '@material-ui/core/Snackbar';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import * as publicInfoClient from '../../utils/publicInfoClient';
import Alert from '../Alert';
import Communication from '../Communication';
import { downloadFile } from '../../utils/contentClient';
import { makeStyles } from '@material-ui/core/styles';

const unsupportedFiles = ['.wk4', '.ehtml', '.rules'];

const getPublicInfo = ({ publicId, environment }) => {
  return publicInfoClient.get(publicId, environment);
};

const isCommunicationFile = (fileName) => {
  return fileName === 'communication.share' || fileName === 'job.share';
};

const parseSearchParams = (searchParams) => {
  if (!searchParams) return [];

  return Object.keys(searchParams).map((key) => ({
    name: key,
    value: searchParams[key],
  }));
};

const useStyles = makeStyles((theme) => {
  return {
    text: {
      margin: '16px',
    },
  };
});

const PublicLink = ({ match, history, location, auth, setAuth }) => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [ready, setReady] = useState(false);

  const url = Utils.url.parseUrl(location.search);
  const searchParams = Utils.url.getSearchParams(location.search);

  const classes = useStyles();
  const parameters = parseSearchParams(searchParams);
  const { data, error, isPending } = useAsync({
    promiseFn: getPublicInfo,
    publicId: url.query.publicId,
    environment: match.params.environment,
  });
  const showToolbar =
    url.query.showToolbar === undefined || url.query.showToolbar === 'true';

  const getToolbarTitle = () => {
    if (!data) return 'Invalid public link';

    const titleTag = data.File.Tags.find((tag) => tag.Name === 'Title');
    let title = titleTag ? titleTag.Value : data.File.Name;
    const extensionIndex = title.lastIndexOf('.');
    if (extensionIndex !== -1) title = title.substring(0, extensionIndex);

    return title;
  };

  const isDownloadable = () => {
    if (!data || !data.File) return false;
    if (
      data.File.Name.endsWith('.pdf') === true ||
      data.File.Name.endsWith('.docx') === true
    ) {
      return true;
    }
    return false;
  };

  const download = () => {
    const filePath = data.File.FilePath;
    const workspaceName = data.File.WorkspaceName;
    const accessToken = data.AccessToken;
    downloadFile(workspaceName, filePath, accessToken).catch((err) => {
      setSnackbarMessage('Could not download file: ' + err.message);
      setOpenSnackbar(true);
    });
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  const getExtension = (fileName) => {
    const extensionIndex = fileName.lastIndexOf('.');
    if (extensionIndex === -1) return 'unknown';

    return fileName.substring(extensionIndex);
  };

  const isPreviewable = (fileName) => {
    const extension = getExtension(fileName);
    if (extension === 'unknown') return false;
    return !unsupportedFiles.includes(extension);
  };

  const isCommunication = data && isCommunicationFile(data.File.Name);

  useEffect(() => {
    if (!isCommunication && data && data.AccessToken)
      setAuth({ token: data.AccessToken });
    if (!isPending) setReady(true);
  }, [isPending, data, isCommunication]);

  return (
    <div>
      {showToolbar && !isPending && !isCommunication && ready && (
        <Page.ToolBar
          color="navbar"
          leftChildren={getToolbarTitle()}
          centerChildren={
            isDownloadable() && (
              <Button
                color="inherit"
                startIcon={<Icon type="download" />}
                onClick={() => download()}
              >
                Download
              </Button>
            )
          }
          icon={
            url.query.closeUrl && {
              type: 'close',
              onClick: () => {
                window.location = url.query.closeUrl;
              },
            }
          }
        />
      )}

      {!isCommunication && (
        <Page.Common margin="none">
          {error && <Alert>The public link is no longer valid.</Alert>}
          {data &&
            ready &&
            (isPreviewable(data.File.Name) ? (
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: 0,
                  bottom: 0,
                }}
              >
                <DocumentPreview
                  workspace={data.File.WorkspaceName}
                  path={data.File.FilePath}
                  fileName={data.File.Name}
                  accessToken={data.AccessToken}
                />
              </div>
            ) : (
              <Typography align="left" variant="body2" className={classes.text}>
                {`Preview is not available for files of this format: "${getExtension(
                  data.File.Name
                )}".`}
              </Typography>
            ))}
        </Page.Common>
      )}
      {isCommunication && (
        <div className="publicLink-communication-wrapper">
          <Communication.Public
            data={data}
            parameters={parameters}
            showToolbar={showToolbar}
          />
        </div>
      )}
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        action={<Icon type="close" onClick={handleSnackbarClose} />}
      />
    </div>
  );
};

export default withAuth(PublicLink);
